import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserWithProfile, FeedbackItem, FeedbackWithProfile, ServiceWithReports } from "@/types/admin";

interface NewsletterSubscription {
  id: string;
  email: string;
  subscribed_at: string;
  is_active: boolean;
  user_id: string | null;
}

export const useAdminData = (isAdmin: boolean) => {
  const { user, session } = useAuth();
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [feedback, setFeedback] = useState<FeedbackWithProfile[]>([]);
  const [services, setServices] = useState<ServiceWithReports[]>([]);
  const [newsletterSubscriptions, setNewsletterSubscriptions] = useState<NewsletterSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);

  const fetchNewsletterSubscriptions = useCallback(async () => {
    if (!user || !isAdmin) return;

    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .select('*')
        .order('subscribed_at', { ascending: false });

      if (error) {
        console.error('Error fetching newsletter subscriptions:', error);
        return;
      }

      setNewsletterSubscriptions(data || []);
    } catch (error) {
      console.error('Error fetching newsletter subscriptions:', error);
    }
  }, [user, isAdmin]);

  const fetchFeedback = useCallback(async () => {
    if (!user || !isAdmin || !session) return;

    try {
      // Fetch feedback
      const { data: feedbackData, error: feedbackError } = await supabase
        .from("feedback")
        .select("*")
        .order("created_at", { ascending: false });

      if (feedbackError) {
        console.error("Error fetching feedback:", feedbackError);
        throw feedbackError;
      }

      // Fetch all profiles to match with feedback
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name");

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      }

      // Create a map of user profiles
      const profilesMap = new Map();
      if (profilesData) {
        profilesData.forEach((profile) => {
          profilesMap.set(profile.id, profile);
        });
      }

      // Combine feedback with user data - ensuring all required properties are included
      const feedbackWithProfiles: FeedbackWithProfile[] = (feedbackData || []).map((item: FeedbackItem) => {
        const profile = profilesMap.get(item.user_id);
        const userName = profile 
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User'
          : 'Unknown User';

        return {
          ...item,
          user_name: userName,
          resolved_at: item.resolved_at || null,
          resolved_by: item.resolved_by || null,
          admin_response: item.admin_response || null
        };
      });

      setFeedback(feedbackWithProfiles);
    } catch (error: any) {
      console.error("Error fetching feedback:", error);
    }
  }, [user, isAdmin, session]);

  const refreshServices = useCallback(async () => {
    if (!user || !isAdmin) return;

    try {
      setServicesLoading(true);
      console.log("[ADMIN] Refreshing services...");

      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select(`
          *,
          service_categories:category_id (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (servicesError) {
        console.error("[ADMIN] Error fetching services:", servicesError);
        throw servicesError;
      }

      if (!servicesData || servicesData.length === 0) {
        console.log("[ADMIN] No services found in database");
        setServices([]);
        return;
      }

      const userIds = [...new Set(servicesData.map(service => service.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) {
        console.error("[ADMIN] Error fetching profiles:", profilesError);
      }

      const profilesMap = new Map();
      if (profilesData) {
        profilesData.forEach((profile) => {
          profilesMap.set(profile.id, profile);
        });
      }

      const { data: reportCounts, error: reportsError } = await supabase
        .from("service_reports")
        .select("service_id");

      if (reportsError) {
        console.error("[ADMIN] Error fetching reports:", reportsError);
      }

      const { data: activePolls, error: pollsError } = await supabase
        .from("service_deletion_polls")
        .select("service_id")
        .eq("is_active", true);

      if (pollsError) {
        console.error("[ADMIN] Error fetching polls:", pollsError);
      }

      const reportCountMap = new Map();
      reportCounts?.forEach((report) => {
        const count = reportCountMap.get(report.service_id) || 0;
        reportCountMap.set(report.service_id, count + 1);
      });

      const activePollsSet = new Set(activePolls?.map(poll => poll.service_id) || []);

      const servicesWithReports: ServiceWithReports[] = servicesData.map((service) => {
        let serviceLocation = null;
        if (service.service_location) {
          try {
            serviceLocation = typeof service.service_location === 'string' 
              ? JSON.parse(service.service_location) 
              : service.service_location;
          } catch (e) {
            console.error("[ADMIN] Error parsing service location for service", service.id, ":", e);
          }
        }

        const userProfile = profilesMap.get(service.user_id);

        const processedService = {
          ...service,
          pricing_type: service.pricing_type as "fixed" | "hourly",
          delivery_method: service.delivery_method as "virtual" | "in-person" | "both" | null,
          service_location: serviceLocation,
          profiles: userProfile || null,
          report_count: reportCountMap.get(service.id) || 0,
          has_active_poll: activePollsSet.has(service.id)
        };

        return processedService;
      });

      // Sort by report count (flagged services first), then by creation date
      servicesWithReports.sort((a, b) => {
        if (a.report_count !== b.report_count) {
          return b.report_count - a.report_count;
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      console.log("[ADMIN] Refreshed services count:", servicesWithReports.length);
      setServices(servicesWithReports);

    } catch (error: any) {
      console.error("[ADMIN] Critical error in refreshServices:", error);
      toast.error(`Failed to refresh services: ${error?.message || 'Unknown error'}`);
    } finally {
      setServicesLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !isAdmin || !session) return;

      try {
        setLoading(true);

        // Fetch users using edge function
        const { data: usersResponse, error: usersError } = await supabase.functions.invoke('get-users', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (usersError) throw usersError;

        if (usersResponse?.users) {
          setUsers(usersResponse.users);
        }

        await Promise.all([
          fetchFeedback(),
          fetchNewsletterSubscriptions()
        ]);

      } catch (error: any) {
        console.error("Error fetching admin data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user && isAdmin) {
      fetchData();
    }
  }, [user, isAdmin, session, fetchFeedback, fetchNewsletterSubscriptions]);

  useEffect(() => {
    if (user && isAdmin) {
      refreshServices();
    }
  }, [user, isAdmin, refreshServices]);

  // Set up real-time subscriptions for feedback updates
  useEffect(() => {
    if (!user || !isAdmin) return;

    const feedbackChannel = supabase
      .channel('feedback-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feedback'
        },
        () => {
          console.log('Feedback updated, refreshing...');
          fetchFeedback();
        }
      )
      .subscribe();

    const newsletterChannel = supabase
      .channel('newsletter-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'newsletter_subscriptions'
        },
        () => {
          console.log('Newsletter subscriptions updated, refreshing...');
          fetchNewsletterSubscriptions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(feedbackChannel);
      supabase.removeChannel(newsletterChannel);
    };
  }, [user, isAdmin, fetchFeedback, fetchNewsletterSubscriptions]);

  return {
    users,
    feedback,
    services,
    newsletterSubscriptions,
    loading,
    servicesLoading,
    setServices,
    refreshServices,
    refreshFeedback: fetchFeedback
  };
};
